import React, {useEffect, useState} from "react";
import {useAuth} from "../../utils/hooks/useAuth";
import {useConfirm} from "material-ui-confirm";
import {useNavigate, useParams} from "react-router-dom";
import {useNotification} from "../../utils/hooks/useNotification";
import useSWR from "swr";
import {deleteFetcher, getFetcher, postFetcher, putFetcher} from "../../utils/api/fetchers";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import UserRole from "../../schemas/user/roles";
import Loading from "../../components/status/Loading";
import ErrorMessage from "../../components/status/ErrorMessage";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    IconButton,
    Paper, Skeleton,
    Stack,
    TextField, Tooltip,
    Typography
} from "@mui/material";
import CustomRichTextEditor from "../../components/input/CustomRichTextEditor";
import {PublishStatus, RaceDetail, raceDetailSchema} from "../../schemas/races/racesSchema";
import {Club} from "../../schemas/club/clubSchema";
import {Season} from "../../schemas/seasons/seasonsSchema";
import {UserAccount} from "../../schemas/user/userAccountSchemas";
import {DatePicker, LocalizationProvider, TimePicker} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DownloadIcon from "@mui/icons-material/Download";
import ClearIcon from "@mui/icons-material/Clear";
import RacePublishDialog from "../../components/dialog/RacePublishDialog";
import {Attachment} from "../../schemas/attachments/attachmentsSchema";
import {getAttachmentUrl} from "../../utils/api/attachmentUrlBuilder";
import {isFileTypeAllowed} from "../../utils/races/checkAllowedFileType";

const RaceDetails: React.FC = () => {
        const {hasPermission} = useAuth();
        const confirm = useConfirm();
        const navigate = useNavigate();
        const {raceId} = useParams<{ raceId: string }>();
        const {showNotification} = useNotification();
        const {data: clubs} = useSWR<Club[]>("/clubs/all", getFetcher);
        const {data: seasons} = useSWR<Season[]>("/seasons/all", getFetcher);
        const {data: organisers} = useSWR<UserAccount[]>(`/roles/${UserRole.ORGANIZER}/users`, getFetcher);
        const {
            data: attachments,
            mutate: mutateAttachments
        } = useSWR<Attachment[]>(`/races/${raceId}/attachments`, getFetcher);
        const {data: race, isLoading, error, mutate} = useSWR<RaceDetail>(`/races/${raceId}`, getFetcher);
        const {
            register,
            handleSubmit,
            setValue,
            reset,
            formState: {errors, isDirty},
            watch,
            trigger,
        } = useForm<RaceDetail>({
            resolver: zodResolver(raceDetailSchema),
        });
        const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
        const [newAttachments, setNewAttachments] = useState<File[]>([]);
        const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>([]);

        const [editMode, setEditMode] = useState<boolean>(false);
        const readOnly = !hasPermission(UserRole.COMMODORE);
        const noEdit = readOnly || !editMode;

        useEffect(() => {
            if (race) {
                reset(race);
            }
        }, [race, reset]);

        const onSubmit = async (data: RaceDetail) => {
            if (!race) return;
            if (!isDirty && newAttachments.length === 0 && removedAttachmentIds.length === 0) {
                showNotification("Nebyla provedena žádná změna", "info");
                setEditMode(false);
                return;
            }

            try {
                // update race data
                if (isDirty) {
                    await putFetcher(`/races/${raceId}`, {
                        raceSeasonId: data.raceSeason.id,
                        clubId: data.club?.id, ...data
                    });
                }

                // upload new attachments
                const failedUploads: string[] = [];
                if (newAttachments.length > 0) {
                    for (const file of newAttachments) {
                        const formDataObj = new FormData();
                        formDataObj.append("file", file);

                        try {
                            await postFetcher<FormData, void>(
                                `/races/${race.id}/attachments`,
                                formDataObj,
                                {headers: {'Content-Type': 'multipart/form-data'}}
                            );
                        } catch (error: any) {
                            console.error(`Upload error, file ${file.name}:`, error);
                            failedUploads.push(file.name);
                        }
                    }
                }

                // delete removed attachments
                const failedDeletions: string[] = [];
                if (removedAttachmentIds.length > 0) {
                    for (const id of removedAttachmentIds) {
                        try {
                            await deleteFetcher(`/races/${race.id}/attachments/${id}`);
                        } catch (error: any) {
                            console.error(`Delete error, file ${id}:`, error);
                            failedDeletions.push(id.toString());
                        }
                    }
                }

                if (failedUploads.length > 0 || failedDeletions.length > 0) {
                    showNotification(
                        `Závod byl upraven, ale některé přílohy se nepodařilo zpracovat: ${[...failedUploads, ...failedDeletions].join(', ')}`,
                        "warning"
                    );
                } else {
                    showNotification("Závod byl úspěšně upraven", "success");
                }

                setRemovedAttachmentIds([]);
                setNewAttachments([]);
                setEditMode(false);
                await mutate();
                await mutateAttachments();
            } catch (error: any) {
                console.error(error);
                if (error.response?.status === 409) {
                    showNotification("Závod s tímto jménem a datem již existuje", "error");
                } else {
                    showNotification("Nepodařilo se upravit závod", "error");
                }
            }
        };

        const handleDelete = () => {
            confirm({
                description: `Opravdu chcete smazat tento závod?`,
                title: "Smazat závod",
                confirmationText: "Smazat",
                cancellationText: "Zrušit",
                confirmationButtonProps: {variant: "contained", color: "error"},
            })
                .then(() => {
                    deleteFetcher(`/races/${raceId}`).then(() => {
                        showNotification('Závod byl smazán', 'success');
                        navigate('/races');
                    }).catch(() => {
                        showNotification('Závod se nepodařilo smazat', 'error');
                    });
                })
                .catch(() => {
                });
        };

        const handlePublishConfirm = async (status: PublishStatus) => {
            putFetcher(`/races/${raceId}/status/publish`, {publishStatus: status})
                .then(() => {
                    showNotification("Umístění publiakce úspěšně změněno", "success");
                    mutate();
                })
                .catch((error) => {
                    if (error.response?.status === 404) {
                        showNotification("Závod nenalezen", "error");
                    } else {
                        showNotification("Nepodařilo se změnit stav publikace závodu", "error");
                    }
                })
                .finally(() => {
                    setIsDialogOpen(false);
                });
        };

        const createPublishChip = (status: PublishStatus) => {
            switch (status) {
                case "ANNOUNCED":
                    return <Chip
                        label="Publikováno v kalendáři"
                        color="primary"
                    />;
                case "PUBLISHED":
                    return <Chip
                        label="Publikováno v kalendáři i přehledu aktualit"
                        color="success"
                    />;
                case "UNPUBLISHED":
                    return <Chip
                        label="Nepubikováno"
                        color="error"
                    />;
                default:
                    return <Skeleton/>
            }
        }

        const handleAttachmentAdd = (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = event.target.files;
            if (files) {
                const newFiles = Array.from(files);

                const validFiles = newFiles.filter(file => isFileTypeAllowed(file.type));
                if (validFiles.length < newFiles.length) {
                    showNotification("Některé soubory nebyly přidány – povolené typy jsou PDF a obrázky", "warning");
                }

                setNewAttachments((prev) => [...prev, ...validFiles]);
                event.target.value = ''; // reset input value to allow re-uploading the same file
            }
        };

        const downloadOrPreview = async (file: Attachment, preview: boolean) => {
            if (!race) return;

            const url = getAttachmentUrl(race.id, file.id);
            const blobUrl = await getFetcher<Blob>(url, {
                responseType: "blob",
            })
                .then((blob) => URL.createObjectURL(blob))
                .catch(() => {
                    showNotification("Nepodařilo se stáhnout soubor", "error");
                });

            if (blobUrl === undefined) {
                showNotification("Nepodařilo se stáhnout soubor", "error");
                return;
            }

            if (preview) {
                const newWindow = window.open(blobUrl, "_blank");
                if (newWindow) {
                    newWindow.focus();
                } else {
                    showNotification("Zablokováno vyskakovací okno", "error");
                }
            } else {
                const link = document.createElement("a");
                link.href = blobUrl
                link.download = file.name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        };

        if (isLoading || !race || !clubs || !seasons || !organisers || !attachments) return <Loading/>;
        if (error) return <ErrorMessage/>

        return (
            <Paper elevation={3} sx={{padding: 4, maxWidth: 800, margin: '0 auto'}}>
                <Stack direction={{xs: "column", sm: "row"}} justifyContent="space-between">
                    <Typography variant="h4" gutterBottom>
                        Detail závodu
                    </Typography>
                    {!readOnly && (
                        <Stack direction={{xs: "column-reverse", sm: "row"}} spacing={1} alignItems="center">
                            <Button
                                title="Změnit umístění publikace"
                                variant="outlined"
                                onClick={() => setIsDialogOpen(true)}
                                size="small"
                            >
                                Změna publikace
                            </Button>
                            {createPublishChip(watch("publishStatus"))}
                        </Stack>
                    )}
                </Stack>
                <Box component="form" noValidate>
                    <Stack spacing={3}>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}>
                            <TextField
                                autoFocus
                                fullWidth
                                label="Jméno"
                                variant="standard"
                                required
                                {...register('name')}
                                error={!!errors.name}
                                helperText={errors.name?.message}
                                InputProps={{readOnly: noEdit}}
                            />
                            <Autocomplete
                                fullWidth
                                options={seasons}
                                getOptionLabel={(option) => option.year.toString()}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => <TextField
                                    {...params}
                                    label="Sezóna"
                                    variant="standard"
                                    error={!!errors.raceSeason}
                                    helperText={errors.raceSeason?.message}
                                    required
                                />}
                                readOnly={noEdit}
                                value={watch('raceSeason') ?? seasons.find(season => season.id === race.raceSeason.id)}
                                onChange={(_, value) => {
                                    if (value) setValue('raceSeason', value, {shouldDirty: true, shouldValidate: true})
                                }}
                                disableClearable
                                sx={{width: {xs: '100%', sm: '35%'}}}
                            />
                        </Stack>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} justifyContent="space-between">
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="cs">
                                <DatePicker
                                    label="Datum"
                                    format="DD.MM.YYYY"
                                    {...register('date')}
                                    onChange={(value) => {
                                        if (value?.isValid()) {
                                            setValue('date', value.format("YYYY-MM-DD"), {
                                                shouldDirty: true,
                                                shouldValidate: true
                                            });
                                        } else {
                                            setValue('date', '', {shouldDirty: true, shouldValidate: true});
                                        }
                                    }}
                                    value={watch('date') ? dayjs(watch('date'), "YYYY-MM-DD") : null}
                                    readOnly={noEdit}
                                    slotProps={{
                                        textField: {
                                            variant: 'standard',
                                            fullWidth: true,
                                            error: !!errors.date,
                                            helperText: errors.date?.message,
                                            required: true,
                                        },
                                        field: {clearable: false}
                                    }}
                                />
                                <TimePicker
                                    label="Čas startu"
                                    {...register('time')}
                                    onChange={(value) => {
                                        setValue('time', value?.format("HH:mm"), {
                                            shouldDirty: true,
                                            shouldValidate: true
                                        })
                                    }}
                                    format="HH:mm"
                                    value={watch('time') ? dayjs(watch('time'), "HH:mm") : null}
                                    readOnly={noEdit}
                                    slotProps={{
                                        textField: {
                                            variant: 'standard', fullWidth: true,
                                            error: !!errors.time,
                                            helperText: errors.time?.message,
                                        },
                                        field: {clearable: true}
                                    }}
                                />
                            </LocalizationProvider>
                        </Stack>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}>
                            <TextField
                                fullWidth
                                label="Místo konání"
                                variant="standard"
                                {...register('location')}
                                error={!!errors.location}
                                helperText={errors.location?.message}
                                InputProps={{readOnly: noEdit}}
                            />
                            <Autocomplete
                                fullWidth
                                options={clubs}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => <TextField
                                    {...params}
                                    label="Pořádající klub"
                                    variant="standard"
                                    error={!!errors.club}
                                    helperText={errors.club?.message}
                                    required
                                />}
                                readOnly={noEdit}
                                value={watch('club') ?? clubs.find(club => race.club && club.id === race.club.id)}
                                onChange={(_, value) => setValue('club', value, {shouldDirty: true, shouldValidate: true})}
                            />
                        </Stack>
                        <CustomRichTextEditor
                            title="Popis"
                            value={watch('description') ?? ""}
                            onChange={(value) => setValue('description', value, {shouldDirty: true, shouldValidate: true})}
                            error={errors.description?.message}
                            readOnly={noEdit}
                        />
                        <Autocomplete
                            multiple
                            limitTags={2}
                            options={organisers}
                            value={
                                (watch('organizersIds') ?? [])
                                    .map((id) => organisers.find((o) => o.id === id))
                                    .filter((o): o is UserAccount => o !== undefined)
                            }
                            getOptionLabel={(option) => `${option.firstName} ${option.lastName} - ${option.email}`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => <TextField
                                {...params}
                                label="Pořadatelé"
                                variant="standard"
                                error={!!errors.organizersIds}
                                helperText={errors.organizersIds?.message}
                            />}
                            disableCloseOnSelect
                            renderOption={(props, option, {selected}) => {
                                const {key, ...optionProps} = props;
                                return (
                                    <li key={key} {...optionProps}>
                                        <Checkbox
                                            icon={<CheckBoxOutlineBlankIcon/>}
                                            checkedIcon={<CheckBoxIcon/>}
                                            style={{marginRight: 8}}
                                            checked={selected}
                                        />
                                        {option.firstName} {option.lastName} - {option.email}
                                    </li>
                                );
                            }}
                            onChange={(_, value) => setValue('organizersIds', value.map((v) => v.id), {
                                shouldDirty: true,
                                shouldValidate: true
                            })}
                            onBlur={() => trigger('organizersIds')}
                            readOnly={noEdit}
                        />
                        <Stack direction="column" spacing={0} alignItems="flex-start">
                            <Typography>Přílohy:</Typography>

                            {attachments
                                .filter((file) => !removedAttachmentIds.includes(file.id))
                                .map((file, index) => (
                                    <Stack
                                        key={index}
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                    >
                                        <Tooltip title="Zobrazit přílohu" arrow placement="top">
                                            <Typography
                                                variant="body2"
                                                onClick={() => downloadOrPreview(file, true)}
                                                sx={{
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    '&:hover': {color: 'primary.main'}
                                                }}
                                            >
                                                {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                            </Typography>
                                        </Tooltip>

                                        <IconButton
                                            title="Stáhnout přílohu"
                                            size="small"
                                            onClick={() => downloadOrPreview(file, false)}
                                        >
                                            <DownloadIcon/>
                                        </IconButton>

                                        {!noEdit && (
                                            <IconButton
                                                title="Odstranit přílohu"
                                                size="small"
                                                color="error"
                                                onClick={() => {
                                                    setRemovedAttachmentIds(prev => [...prev, file.id]);
                                                }}
                                            >
                                                <ClearIcon/>
                                            </IconButton>
                                        )}
                                    </Stack>
                                ))}

                            {newAttachments.map((file, index) => (
                                <Stack
                                    key={"new" + index}
                                    direction="row"
                                    spacing={0.5}
                                    alignItems="center"
                                >
                                    <Tooltip title="Zobrazit přílohu" arrow placement="top">
                                        <Typography
                                            variant="body2"
                                            onClick={() => {
                                                const url = URL.createObjectURL(file);
                                                window.open(url, "_blank");
                                            }}
                                            sx={{
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                '&:hover': {color: 'primary.main'}
                                            }}
                                        >
                                            {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                        </Typography>
                                    </Tooltip>

                                    <IconButton
                                        title="Stáhnout přílohu"
                                        size="small"
                                        onClick={() => {
                                            const url = URL.createObjectURL(file);
                                            const link = document.createElement("a");
                                            link.href = url;
                                            link.download = file.name;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            URL.revokeObjectURL(url);
                                        }}
                                    >
                                        <DownloadIcon/>
                                    </IconButton>

                                    {editMode && (
                                        <IconButton
                                            title="Odstranit přílohu"
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                                setNewAttachments((prev) =>
                                                    prev.filter((_, i) => i !== index)
                                                )
                                            }>
                                            <ClearIcon/>
                                        </IconButton>
                                    )}
                                </Stack>
                            ))}

                            {attachments
                                .filter((file) => !removedAttachmentIds.includes(file.id))
                                .length === 0 && newAttachments.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    Žádné přílohy
                                </Typography>
                            )}

                            {!noEdit && (
                                <Button variant="outlined" component="label" color="success">
                                    Přidat přílohu
                                    <input
                                        type="file"
                                        hidden
                                        multiple
                                        accept="application/pdf, image/*"
                                        onChange={handleAttachmentAdd}
                                    />
                                </Button>
                            )}
                        </Stack>
                        {!readOnly && (
                            editMode ? (
                                <Stack direction={{xs: "column-reverse", sm: "row"}} spacing={2}>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        sx={{width: 250}}
                                        onClick={() => {
                                            setEditMode(false);
                                            reset(race);
                                            setRemovedAttachmentIds([]);
                                            setNewAttachments([]);
                                        }}
                                    >
                                        Zrušit změny
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleSubmit(onSubmit)}
                                    >
                                        Uložit
                                    </Button>
                                </Stack>
                            ) : (
                                <Stack direction={{xs: "column-reverse", sm: "row"}} spacing={2}>
                                    {hasPermission(UserRole.ADMIN) && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            sx={{px: 10}}
                                            onClick={handleDelete}
                                        >
                                            Smazat
                                        </Button>
                                    )}
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => setEditMode(true)}
                                    >
                                        Upravit
                                    </Button>
                                </Stack>
                            )
                        )}
                    </Stack>
                </Box>
                <RacePublishDialog
                    open={isDialogOpen}
                    onClose={() => setIsDialogOpen(false)}
                    onConfirm={handlePublishConfirm}
                    newRace={false}
                    defaultStatus={watch("publishStatus") ?? race.publishStatus}
                />
            </Paper>
        );
    }
;

export default RaceDetails;